import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import {
  AddProductCategoryFormDialog,
  EditProductCategoryFormDialog,
  ProductCategoryTypeLabels,
} from "@/features/product-categories";
import { apiClient, statusColors, statusLabels } from "@/features/shared";
import { Add as AddIcon, Edit as EditIcon } from "@mui/icons-material";
import { Button, Chip, IconButton } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { PageContainer } from "@/features/ui";
import { useEffect, useMemo, useState } from "react";

export const ProductCategories = () => {
  const { hasPermission } = useAuth();

  const [productCategories, setProductCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addDialogDefaultValues, setAddDialogDefaultValues] = useState(null);
  const [productCategoryId, setProductCategoryId] = useState(null);

  // Function to fetch all product categories
  const fetchProductCategories = async () => {
    try {
      setLoading(true);

      const {
        data: { data: productCategories },
      } = await apiClient.get("product-categories");

      // Filter out virtual root (parent_id is null)
      const filteredCategories = productCategories.filter(
        (category) => category.parentId !== null,
      );

      setProductCategories(filteredCategories);
    } catch (error) {
      console.error("Failed to fetch product categories:", error);
      setProductCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductCategories();
  }, []);

  // Build tree paths from nested set model (lft/rgt)
  const productCategoriesWithPaths = useMemo(() => {
    if (!productCategories.length) return [];

    // Sort by lft to ensure proper order
    const sortedRows = [...productCategories].sort((a, b) => a.lft - b.lft);

    // Build paths for each row based on nested set model
    return sortedRows.map((row) => {
      const path = [];

      // Find all ancestors (nodes that contain this node)
      sortedRows.forEach((potentialAncestor) => {
        if (
          potentialAncestor.lft < row.lft &&
          potentialAncestor.rgt > row.rgt
        ) {
          path.push(potentialAncestor.name);
        }
      });

      // Add current node to path
      path.push(row.name);

      return {
        ...row,
        path: path,
      };
    });
  }, [productCategories]);

  const toolbarContent = hasPermission(PermissionEnum.CREATE_PRODUCTS) && (
    <Button
      fullWidth
      variant="contained"
      onClick={() => setAddDialogDefaultValues({})}
    >
      Tambah
    </Button>
  );

  const hasCreatePermission = hasPermission(PermissionEnum.CREATE_PRODUCTS);
  const hasUpdatePermission = hasPermission(
    PermissionEnum.UPDATE_PRODUCT_CATEGORIES,
  );

  const columns = [
    {
      field: "type",
      headerName: "Tipe",
      flex: 1,
      renderCell: ({ value }) => ProductCategoryTypeLabels[value],
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: ({ value }) => {
        return <Chip label={statusLabels[value]} color={statusColors[value]} />;
      },
    },
  ];

  if (hasCreatePermission || hasUpdatePermission) {
    columns.push({
      field: "actions",
      type: "actions",
      headerName: "Aksi",
      width: 150,
      getActions: (params) => {
        const actions = [];

        if (hasCreatePermission) {
          actions.push(
            <IconButton
              key="add"
              onClick={() => {
                setAddDialogDefaultValues({
                  hasParent: true,
                  parentProductCategory: params.row,
                  type: params.row.type,
                });
              }}
              size="small"
            >
              <AddIcon />
            </IconButton>,
          );
        }

        if (hasUpdatePermission) {
          actions.push(
            <IconButton
              key="edit"
              onClick={() => setProductCategoryId(params.row.id)}
              size="small"
            >
              <EditIcon />
            </IconButton>,
          );
        }

        return actions;
      },
    });
  }

  return (
    <PageContainer
      maxWidth="xl"
      breadcrumbs={[]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
    >
      <DataGridPro
        rows={productCategoriesWithPaths}
        loading={loading}
        autoHeight
        treeData
        getTreeDataPath={(row) => row.path}
        groupingColDef={{
          headerName: "Nama",
          flex: 2,
        }}
        columns={columns}
        showToolbar
        disableRowSelectionOnClick
      />

      {!!addDialogDefaultValues && (
        <AddProductCategoryFormDialog
          defaultValues={addDialogDefaultValues}
          onClose={() => setAddDialogDefaultValues(null)}
          onSubmitted={() => {
            setAddDialogDefaultValues(null);
            fetchProductCategories();
          }}
        />
      )}

      {!!productCategoryId && (
        <EditProductCategoryFormDialog
          onClose={() => setProductCategoryId(null)}
          productCategoryId={productCategoryId}
          onSubmitted={() => {
            setProductCategoryId(null);
            fetchProductCategories();
          }}
        />
      )}
    </PageContainer>
  );
};
